<template>
  <div class="scrollable only-y">
    <div class="page-header">
      <h1><i :class="'mdi mdi-' + $route.meta.icon"></i> {{ $route.name }}</h1>
      <el-breadcrumb separator="/">
        <el-breadcrumb-item :to="{ path: '/' }">
          <i class="mdi mdi-home-outline"></i>
        </el-breadcrumb-item>
        <el-breadcrumb-item>{{ $route.name }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div class="card-base card-shadow--medium p-30">
      <!-- Filtros -->
      <el-row :gutter="10" class="mb-20">
        <el-col :xs="24" :sm="12" :md="8" :lg="4">
          <el-select v-model="filters.event" placeholder="Evento" clearable @change="handleFilterChange" size="small">
            <el-option
              v-for="event in filterOptions.events"
              :key="event"
              :label="formatEventName(event)"
              :value="event">
            </el-option>
          </el-select>
        </el-col>

        <el-col :xs="24" :sm="12" :md="8" :lg="4">
          <el-select v-model="filters.auditable_type" placeholder="Tipo de Modelo" clearable @change="handleFilterChange" size="small">
            <el-option
              v-for="type in filterOptions.types"
              :key="type"
              :label="formatModelName(type)"
              :value="type">
            </el-option>
          </el-select>
        </el-col>

        <el-col :xs="24" :sm="12" :md="8" :lg="3">
          <el-select v-model="filters.tags" placeholder="Tag" clearable @change="handleFilterChange" size="small">
            <el-option
              v-for="tag in filterOptions.tags"
              :key="tag"
              :label="tag"
              :value="tag">
            </el-option>
          </el-select>
        </el-col>

        <el-col :xs="24" :sm="12" :md="8" :lg="4">
          <el-date-picker
            v-model="filters.date_from"
            type="date"
            placeholder="Fecha desde"
            size="small"
            value-format="yyyy-MM-dd"
            @change="handleFilterChange">
          </el-date-picker>
        </el-col>

        <el-col :xs="24" :sm="12" :md="8" :lg="4">
          <el-date-picker
            v-model="filters.date_to"
            type="date"
            placeholder="Fecha hasta"
            size="small"
            value-format="yyyy-MM-dd"
            @change="handleFilterChange">
          </el-date-picker>
        </el-col>

        <el-col :xs="24" :sm="24" :md="8" :lg="5">
          <el-button type="primary" size="small" @click="handleFilterChange" icon="el-icon-search">Buscar</el-button>
          <el-button size="small" @click="handleResetFilters" icon="el-icon-refresh-left">Limpiar</el-button>
        </el-col>
      </el-row>

      <!-- Búsqueda general -->
      <el-row :gutter="10" class="mb-20">
        <el-col :xs="24" :sm="24" :md="12" :lg="10">
          <el-input
            v-model="filters.search"
            placeholder="Buscar en valores antiguos/nuevos, URL, IP..."
            size="small"
            clearable
            @clear="handleFilterChange"
            @keyup.enter.native="handleFilterChange">
            <el-button slot="append" icon="el-icon-search" @click="handleFilterChange"></el-button>
          </el-input>
        </el-col>
      </el-row>

      <!-- Tabla de auditorías -->
      <el-table
        :data="audits"
        v-loading="loading"
        height="500"
        class="audit-table"
        :fit="true">
        <el-table-column type="expand">
          <template slot-scope="props">
            <div class="audit-detail">
              <el-row :gutter="20">
                <el-col :span="12">
                  <div class="detail-section detail-section-old">
                    <h4>
                      <i class="el-icon-back"></i>
                      Valores Anteriores
                    </h4>
                    <div v-if="props.row.old_values" class="json-container json-container-old">
                      <pre>{{ formatJSON(props.row.old_values) }}</pre>
                    </div>
                    <el-empty v-else description="Sin valores anteriores" :image-size="60"></el-empty>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="detail-section detail-section-new">
                    <h4>
                      <i class="el-icon-right"></i>
                      Valores Nuevos
                    </h4>
                    <div v-if="props.row.new_values" class="json-container json-container-new">
                      <pre>{{ formatJSON(props.row.new_values) }}</pre>
                    </div>
                    <el-empty v-else description="Sin valores nuevos" :image-size="60"></el-empty>
                  </div>
                </el-col>
              </el-row>
              <el-divider></el-divider>
              <el-row :gutter="20" class="metadata-row">
                <el-col :span="8">
                  <div class="metadata-item">
                    <i class="el-icon-link"></i>
                    <div>
                      <span class="metadata-label">URL:</span>
                      <span class="metadata-value">{{ props.row.url || 'N/A' }}</span>
                    </div>
                  </div>
                </el-col>
                <el-col :span="8">
                  <div class="metadata-item">
                    <i class="el-icon-location-information"></i>
                    <div>
                      <span class="metadata-label">IP:</span>
                      <span class="metadata-value">{{ props.row.ip_address || 'N/A' }}</span>
                    </div>
                  </div>
                </el-col>
                <el-col :span="8">
                  <div class="metadata-item">
                    <i class="el-icon-monitor"></i>
                    <div>
                      <span class="metadata-label">User Agent:</span>
                      <span class="metadata-value">{{ props.row.user_agent || 'N/A' }}</span>
                    </div>
                  </div>
                </el-col>
              </el-row>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column label="ID" prop="id" width="70" align="center"></el-table-column>
        
        <el-table-column label="Evento" min-width="180">
          <template slot-scope="scope">
            <el-tag :type="getEventTagType(scope.row.event)" size="small" effect="plain">
              {{ formatEventName(scope.row.event) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Modelo" min-width="150">
          <template slot-scope="scope">
            <el-tooltip :content="scope.row.auditable_type" placement="top">
              <span class="model-name">
                <i class="el-icon-document"></i>
                {{ formatModelName(scope.row.auditable_type) }}
              </span>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column label="ID" width="80" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" type="info" effect="plain">{{ scope.row.auditable_id }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Usuario" min-width="140">
          <template slot-scope="scope">
            <span class="user-info">
              <i class="el-icon-user"></i>
              {{ scope.row.user ? scope.row.user.name : 'Sistema' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Tags" min-width="130">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.tags" size="small" type="primary" effect="plain">
              <i class="el-icon-price-tag"></i>
              {{ scope.row.tags }}
            </el-tag>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>

        <el-table-column label="Fecha" min-width="160">
          <template slot-scope="scope">
            <div class="date-info">
              <i class="el-icon-time"></i>
              <span>{{ formatDate(scope.row.created_at) }}</span>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- Paginación -->
      <el-row class="mt-20">
        <el-col :span="24" style="text-align: right;">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.current_page"
            :page-sizes="[10, 15, 25, 50, 100]"
            :page-size="pagination.per_page"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total">
          </el-pagination>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'audit-log',
  data() {
    return {
      filters: {
        event: '',
        auditable_type: '',
        user_id: '',
        tags: '',
        date_from: '',
        date_to: '',
        search: ''
      }
    }
  },
  computed: {
    ...mapState('audit', ['audits', 'pagination', 'loading', 'filterOptions'])
  },
  mounted() {
    this.loadData()
  },
  methods: {
    ...mapActions('audit', [
      'fetchAudits',
      'fetchFilterOptions',
      'setFilters',
      'resetFilters',
      'setPage',
      'setPerPage'
    ]),
    
    async loadData() {
      await this.fetchAudits()
      await this.fetchFilterOptions()
    },
    
    handleFilterChange() {
      this.setFilters(this.filters)
      this.setPage(1)
      this.fetchAudits()
    },
    
    handleResetFilters() {
      this.filters = {
        event: '',
        auditable_type: '',
        user_id: '',
        tags: '',
        date_from: '',
        date_to: '',
        search: ''
      }
      this.resetFilters()
      this.fetchAudits()
    },
    
    handleSizeChange(val) {
      this.setPerPage(val)
      this.fetchAudits()
    },
    
    handleCurrentChange(val) {
      this.setPage(val)
      this.fetchAudits()
    },
    
    formatEventName(event) {
      const eventNames = {
        'created': 'Creado',
        'updated': 'Actualizado',
        'deleted': 'Eliminado',
        'inventory_adjustment': 'Ajuste de Inventario',
        'inventory_adjustment_detail': 'Detalle de Ajuste',
        'inventory_adjustment_updated': 'Ajuste Actualizado',
        'inventory_adjustment_detail_added': 'Detalle Agregado',
        'inventory_adjustment_detail_deleted': 'Detalle Eliminado',
        'inventory_adjustment_cancelled': 'Ajuste Anulado',
        'document_deleted': 'Documento Eliminado'
      }
      return eventNames[event] || event
    },
    
    formatModelName(model) {
      if (!model) return 'N/A'
      const parts = model.split('\\')
      return parts[parts.length - 1]
    },
    
    getEventTagType(event) {
      const types = {
        'created': 'success',
        'updated': 'warning',
        'deleted': 'danger',
        'inventory_adjustment': 'primary',
        'inventory_adjustment_detail': 'info',
        'inventory_adjustment_updated': 'warning',
        'inventory_adjustment_detail_added': 'success',
        'inventory_adjustment_detail_deleted': 'danger',
        'inventory_adjustment_cancelled': 'danger',
        'document_deleted': 'danger'
      }
      return types[event] || 'info'
    },
    
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    
    formatJSON(obj) {
      if (typeof obj === 'string') {
        try {
          obj = JSON.parse(obj)
        } catch (e) {
          return obj
        }
      }
      return JSON.stringify(obj, null, 2)
    }
  }
}
</script>

<style scoped>
.mb-20 {
  margin-bottom: 20px;
}

.mt-20 {
  margin-top: 20px;
}

.audit-detail {
  padding: 30px;
  background: linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%);
  border-radius: 8px;
}

.detail-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;
}

.detail-section-old {
  border-left: 4px solid #e6a23c;
}

.detail-section-old h4 {
  color: #e6a23c;
}

.detail-section-old h4 i {
  color: #e6a23c;
}

.detail-section-new {
  border-left: 4px solid #67c23a;
}

.detail-section-new h4 {
  color: #67c23a;
}

.detail-section-new h4 i {
  color: #67c23a;
}

.detail-section h4 {
  margin: 0 0 15px 0;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e4e7ed;
}

.json-container {
  border-radius: 6px;
  border: 1px solid #e4e7ed;
  overflow: hidden;
}

.json-container-old {
  background-color: #fdf6ec;
  border-color: #f5dab1;
}

.json-container-new {
  background-color: #f0f9ff;
  border-color: #c6e2b5;
}

.json-container pre {
  background-color: transparent;
  padding: 15px;
  margin: 0;
  max-height: 300px;
  overflow-y: auto;
  font-size: 12px;
  line-height: 1.6;
  color: #2c3e50;
  font-family: 'Courier New', Courier, monospace;
}

.metadata-row {
  margin-top: 20px;
}

.metadata-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 15px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

.metadata-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.metadata-item i {
  color: #409eff;
  font-size: 20px;
  margin-top: 2px;
}

.metadata-item > div {
  flex: 1;
  min-width: 0;
}

.metadata-label {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
  font-weight: 500;
}

.metadata-value {
  display: block;
  font-size: 13px;
  color: #606266;
  word-break: break-all;
  line-height: 1.4;
}

.text-muted {
  color: #909399;
  font-style: italic;
}

.el-select,
.el-date-picker {
  width: 100%;
}

.audit-table {
  width: 100% !important;
}

.audit-table >>> .el-table__header-wrapper,
.audit-table >>> .el-table__body-wrapper {
  width: 100% !important;
}

.audit-table >>> table {
  width: 100% !important;
}

.audit-table >>> .el-table__header,
.audit-table >>> .el-table__body {
  width: 100% !important;
  table-layout: auto !important;
}

.model-name,
.user-info,
.date-info {
  display: flex;
  align-items: center;
  gap: 6px;
}

.model-name i,
.user-info i,
.date-info i {
  color: #909399;
  font-size: 14px;
}

.model-name {
  cursor: help;
  color: #606266;
  font-weight: 500;
}

.user-info {
  color: #606266;
}

.date-info {
  color: #606266;
  font-size: 13px;
}

.date-info span {
  white-space: nowrap;
}

.audit-table >>> .el-tag {
  font-weight: 500;
}

.audit-table >>> .el-tag i {
  margin-right: 4px;
}
</style>
